// BioForge 2.5 — Final Venture Report tab: long-form investor memorandum.
import { useState, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Download, Printer, Share2, Quote, FileText, CheckCircle2, XCircle,
  Wrench, Activity, ShieldCheck, FlaskConical, Building2, AlertOctagon,
  Lightbulb, ListTree,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { TechConvergence } from "@/viz";
import { formatUSD, API } from "@/lib";
import { formatINR } from "@/currency";
import { LAYER_INFO, resolveLayer } from "@/layers";

const ReportDocContext = createContext(null);

const SEV = {
  Low:      { bg: "bg-emerald-400/10", text: "text-emerald-200", border: "border-emerald-400/30" },
  Medium:   { bg: "bg-amber-400/10",   text: "text-amber-200",   border: "border-amber-400/30" },
  High:     { bg: "bg-orange-400/10",  text: "text-orange-200",  border: "border-orange-400/30" },
  Critical: { bg: "bg-rose-400/15",    text: "text-rose-200",    border: "border-rose-400/40" },
};

export function FinalReport({ doc, id }) {
  if (!doc?.final_report) {
    return <div className="p-12 text-center text-sm text-white/40 italic" data-testid="report-empty">Final report unlocks once the venture pipeline completes.</div>;
  }

  const fr = doc.final_report;
  const fv = fr.founder_review || doc.founder_review || {};
  const sc = doc.scores || fr.venture_scorecard || {};
  const ia = doc.outputs?.innovation_architect || {};
  const pt = doc.outputs?.prototype_designer || {};
  const vp = doc.outputs?.validation_planner || {};
  const rg = doc.outputs?.regulatory_planner || {};
  const ct = doc.outputs?.clinical_trial_designer || {};
  const co = doc.outputs?.commercialization_agent || {};
  const pds = fr.pitch_deck_summary || {};

  const generatedDate = doc.completed_at || doc.created_at;
  const dateLabel = generatedDate
    ? new Date(generatedDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "";

  const downloadJSON = async () => {
    try {
      const res = await fetch(`${API}/runs/${id}`);
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (fr.company_name || "venture").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      a.download = `aignoz-${safeName}-${id.slice(0, 8)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("JSON downloaded");
    } catch (e) { toast.error("Download failed"); }
  };

  const printReport = () => {
    document.body.classList.add("printing-report");
    // Inline !important on the platform badge beats stylesheet — hide it directly.
    const badge = document.getElementById("emergent-badge");
    const prevDisplay = badge ? badge.style.display : null;
    if (badge) badge.style.setProperty("display", "none", "important");
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.body.classList.remove("printing-report");
        if (badge) {
          if (prevDisplay) badge.style.setProperty("display", prevDisplay, "important");
          else badge.style.removeProperty("display");
        }
      }, 500);
    }, 60);
  };

  const copyShareLink = async () => {
    const link = `${window.location.origin}/projects/${id}/report`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Share link copied");
    } catch (e) { toast.error("Couldn't copy"); }
  };

  // Bill of materials totals — guard against non-numeric qty/cost from LLM
  const bom = pt.bill_of_materials || [];
  const numQty = (q) => {
    if (typeof q === "number") return q;
    if (typeof q === "string") {
      const m = q.match(/[\d.]+/);
      return m ? parseFloat(m[0]) : 1;
    }
    return 1;
  };
  const bomTotal = bom.reduce((s, b) => s + (numQty(b.qty) * (Number(b.est_cost_usd) || 0)), 0);

  // 1-line thesis from solution
  const thesis = (pds.solution || fr.solution_summary || "").split(/(?<=[.!?])\s+/)[0].slice(0, 220);

  // Scorecard 8 axes
  const SCORE_AXES = [
    ["innovation_score", "Innovation"],
    ["feasibility_score", "Feasibility"],
    ["regulatory_readiness_score", "Regulatory Readiness"],
    ["clinical_adoption_score", "Clinical Adoption"],
    ["investor_attractiveness_score", "Investor Attractiveness"],
    ["scientific_novelty_score", "Scientific Novelty"],
    ["time_to_market_score", "Time to Market"],
    ["technical_complexity_score", "Technical Complexity"],
  ];

  return (
    <div className="h-full overflow-y-auto scrollbar-thin" data-testid="tab-report">
      <ReportDocContext.Provider value={doc}>
      <article className="report-document max-w-[960px] mx-auto px-10 py-12">

        {/* ─── Executive Header ─── */}
        <header className="report-header" data-testid="report-header">
          <div className="text-[10px] uppercase tracking-[0.22em] text-teal-300/80 mb-3">Venture Memorandum</div>
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-8">
              <h1 data-testid="report-company-name" className="text-5xl font-semibold tracking-tight leading-tight">{fr.company_name}</h1>
              {thesis && (
                <p data-testid="report-thesis" className="text-lg italic text-white/65 leading-relaxed mt-3">
                  {thesis}{thesis.length === 220 && "…"}
                </p>
              )}
            </div>
            <div className="col-span-12 md:col-span-4 grid grid-cols-3 gap-2" data-testid="report-stats">
              <HeaderStat label="Investor" value={sc.investor_attractiveness_score?.score} />
              <HeaderStat label="Innovation" value={sc.innovation_score?.score} />
              <HeaderStat label="Generated" value={dateLabel} small />
            </div>
          </div>
        </header>

        <Divider />

        {/* ─── Export buttons row ─── */}
        <div className="no-print sticky top-0 z-20 -mx-10 px-10 py-3 mb-12 bg-[#0F1116]/70 backdrop-blur-md border-b border-white/[0.06] flex items-center gap-2" data-testid="report-exports">
          <Button onClick={downloadJSON} variant="outline" size="sm" data-testid="download-json">
            <Download className="w-3.5 h-3.5 mr-1.5" />Download JSON
          </Button>
          <Button onClick={printReport} size="sm" className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold" data-testid="print-pdf">
            <Printer className="w-3.5 h-3.5 mr-1.5" />Print as PDF
          </Button>
          <Button onClick={copyShareLink} variant="ghost" size="sm" data-testid="copy-share">
            <Share2 className="w-3.5 h-3.5 mr-1.5" />Copy Share Link
          </Button>
          <div className="flex-1" />
          <div className="text-[10px] mono text-white/30">Run {id.slice(0, 8)}</div>
        </div>

        {/* ─── 1. Problem ─── */}
        <Section n="01" title="Problem" icon={AlertOctagon} testId="sec-problem" agentEvent="problem_analyst">
          <p className="prose">{fr.problem_statement}</p>
        </Section>

        {/* ─── 2. Solution ─── */}
        <Section n="02" title="Solution" icon={Lightbulb} testId="sec-solution" agentEvent="innovation_architect">
          <p className="prose">{fr.solution_summary}</p>
          {ia.concepts?.[0] && (
            <ul className="mt-6 space-y-3">
              {[["Name", ia.concepts[0].name], ["Summary", ia.concepts[0].summary || ia.concepts[0].description], ["Differentiator", ia.concepts[0].differentiator]]
                .filter(([, v]) => !!v)
                .map(([k, v]) => (
                  <li key={k} className="flex gap-3">
                    <span className="shrink-0 text-[10px] uppercase tracking-[0.18em] text-teal-300/80 mt-1 w-32">{k}</span>
                    <span className="text-base text-white/85 leading-relaxed">{v}</span>
                  </li>
                ))}
            </ul>
          )}
        </Section>

        {/* ─── 3. Technology Stack ─── */}
        <Section n="03" title="Technology Stack" icon={ListTree} testId="sec-techstack" agentEvent="technology_matchmaker">
          <div className="flex flex-wrap gap-2">
            {(fr.technology_stack || []).map((t) => (
              <span key={t} className="text-sm px-3 py-1.5 rounded-md bg-teal-400/12 border border-teal-400/30 text-teal-100">{t}</span>
            ))}
          </div>
        </Section>

        {/* ─── 4. Technology Convergence ─── */}
        <Section n="04" title="Technology Convergence" icon={ListTree} testId="sec-convergence" agentEvent="technology_matchmaker">
          <div className="flex justify-center">
            <div className="w-full">
              <TechConvergence problem={doc.inputs?.problem} technologies={fr.technology_stack} companyName={fr.company_name} />
            </div>
          </div>
        </Section>

        {/* ─── 5. Prototype Summary ─── */}
        <Section n="05" title="Prototype Summary" icon={Wrench} testId="sec-prototype" agentEvent="prototype_designer">
          <p className="prose">{pt.system_architecture || fr.prototype_design}</p>
          {pt.modules?.length > 0 && (
            <>
              <SubLabel>Modules</SubLabel>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {pt.modules.map((m, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08]">{m}</span>
                ))}
              </div>
            </>
          )}
          {pt.software_architecture && (
            <>
              <SubLabel>Software architecture</SubLabel>
              <p className="prose text-white/75">{pt.software_architecture}</p>
            </>
          )}
        </Section>

        {/* ─── 6. Bill of Materials ─── */}
        <Section n="06" title="Bill of Materials" icon={ListTree} testId="sec-bom" agentEvent="prototype_designer">
          <div className="rounded-lg border border-white/[0.08] overflow-hidden">
            <table className="w-full text-sm" data-testid="bom-table">
              <thead className="text-[10px] uppercase tracking-[0.18em] text-white/50 bg-white/[0.02]">
                <tr>
                  <th className="text-left px-4 py-2.5">Item</th>
                  <th className="text-right px-4 py-2.5 w-16">Qty</th>
                  <th className="text-right px-4 py-2.5 w-32">Est. Cost (USD)</th>
                  <th className="text-right px-4 py-2.5 w-32">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {bom.map((b, i) => {
                  const q = numQty(b.qty);
                  const cost = Number(b.est_cost_usd) || 0;
                  const sub = q * cost;
                  return (
                    <tr key={i} className={i % 2 ? "bg-white/[0.012]" : ""}>
                      <td className="px-4 py-2 text-white/85">{b.item}</td>
                      <td className="px-4 py-2 text-right mono text-white/70">{b.qty ?? 1}</td>
                      <td className="px-4 py-2 text-right mono text-white/70 leading-tight">
                        <div>${cost.toFixed(2)}</div>
                        <div className="text-[10px] text-white/45">{formatINR(cost)}</div>
                      </td>
                      <td className="px-4 py-2 text-right mono text-teal-200 leading-tight">
                        <div>${sub.toFixed(2)}</div>
                        <div className="text-[10px] text-teal-300/60">{formatINR(sub)}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-white/[0.08] bg-teal-400/[0.04]">
                  <td className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-teal-300/80">Total</td>
                  <td colSpan={2} />
                  <td className="px-4 py-3 text-right mono font-semibold text-teal-100 leading-tight" data-testid="bom-total">
                    <div className="text-sm text-teal-100/70">${bomTotal.toFixed(2)}</div>
                    <div className="text-base text-teal-100">{formatINR(bomTotal)}</div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {(pt.fabrication_approach || pt.assay_design) && (
            <div className="mt-6 grid md:grid-cols-2 gap-6">
              {pt.fabrication_approach && <Box title="Fabrication">{pt.fabrication_approach}</Box>}
              {pt.assay_design && <Box title="Assay">{pt.assay_design}</Box>}
            </div>
          )}
        </Section>

        {/* ─── 7. Validation Strategy ─── */}
        <Section n="07" title="Validation Strategy" icon={Activity} testId="sec-validation" agentEvent="validation_planner">
          <div className="grid md:grid-cols-2 gap-5">
            {vp.analytical_validation_plan && <Box title="Analytical">{vp.analytical_validation_plan}</Box>}
            {vp.technical_validation_plan && <Box title="Technical">{vp.technical_validation_plan}</Box>}
            {vp.pilot_study_design && <Box title="Pilot Study">{vp.pilot_study_design}</Box>}
            {vp.success_criteria?.length > 0 && (
              <Box title="Success Criteria">
                <ul className="space-y-1.5">
                  {vp.success_criteria.map((s, i) => (
                    <li key={i} className="flex gap-2 text-sm"><span className="text-teal-300/70">▸</span>{s}</li>
                  ))}
                </ul>
              </Box>
            )}
          </div>
        </Section>

        {/* ─── 8. Regulatory Pathway ─── */}
        <Section n="08" title="Regulatory Pathway" icon={ShieldCheck} testId="sec-regulatory" agentEvent="regulatory_planner">
          {rg.risk_classification && (
            <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-teal-400/[0.06] border border-teal-400/25">
              <span className="text-[10px] uppercase tracking-[0.14em] text-white/50">Risk classification</span>
              <span className="text-sm font-semibold text-teal-100 mono">{rg.risk_classification}</span>
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-4">
            <Box title="India · CDSCO">{rg.india_cdsco_pathway}</Box>
            <Box title="US · FDA">{rg.fda_pathway}</Box>
            <Box title="EU · CE">{rg.ce_pathway}</Box>
          </div>
          {rg.required_documentation?.length > 0 && (
            <>
              <SubLabel>Required Documentation</SubLabel>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {rg.required_documentation.map((d, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08]">{d}</span>
                ))}
              </div>
            </>
          )}
        </Section>

        {/* ─── 9. Clinical Strategy ─── */}
        <Section n="09" title="Clinical Strategy" icon={FlaskConical} testId="sec-clinical" agentEvent="clinical_trial_designer">
          {ct.protocol_draft && <p className="prose">{ct.protocol_draft}</p>}
          <div className="grid md:grid-cols-2 gap-5 mt-6">
            {ct.inclusion_criteria?.length > 0 && (
              <Box title="Inclusion">
                <ul className="space-y-1.5">
                  {ct.inclusion_criteria.map((c, i) => (
                    <li key={i} className="flex gap-2 text-sm"><span className="text-emerald-300/70 mt-0.5"><CheckCircle2 className="w-3.5 h-3.5" /></span>{c}</li>
                  ))}
                </ul>
              </Box>
            )}
            {ct.exclusion_criteria?.length > 0 && (
              <Box title="Exclusion">
                <ul className="space-y-1.5">
                  {ct.exclusion_criteria.map((c, i) => (
                    <li key={i} className="flex gap-2 text-sm"><span className="text-rose-300/70 mt-0.5"><XCircle className="w-3.5 h-3.5" /></span>{c}</li>
                  ))}
                </ul>
              </Box>
            )}
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3">
            <KpiTile label="Primary endpoint" value={ct.primary_endpoint} small />
            <KpiTile label="Sample size" value={ct.sample_size_estimate?.toLocaleString?.() ?? ct.sample_size_estimate} />
            <KpiTile label="Timeline" value={ct.timeline_months ? `${ct.timeline_months} mo` : "—"} />
          </div>
        </Section>

        {/* ─── 10. Commercialization Strategy ─── */}
        <Section n="10" title="Commercialization Strategy" icon={Building2} testId="sec-commercial" agentEvent="commercialization_agent">
          <div className="grid grid-cols-3 gap-3 mb-6">
            <KpiTile label="TAM" value={formatUSD(co.tam_usd)} subValue={formatINR(co.tam_usd)} accent="teal" />
            <KpiTile label="SAM" value={formatUSD(co.sam_usd)} subValue={formatINR(co.sam_usd)} accent="sky" />
            <KpiTile label="SOM" value={formatUSD(co.som_usd)} subValue={formatINR(co.som_usd)} accent="cyan" />
          </div>
          {co.business_model && (
            <>
              <SubLabel>Business model</SubLabel>
              <p className="prose">{co.business_model}</p>
            </>
          )}
          {co.pilot_partners?.length > 0 && (
            <>
              <SubLabel>Pilot partners</SubLabel>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {co.pilot_partners.map((p, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08]">{p}</span>
                ))}
              </div>
            </>
          )}
          {co.commercialization_roadmap && (
            <>
              <SubLabel>Roadmap</SubLabel>
              <p className="prose">{co.commercialization_roadmap}</p>
            </>
          )}
        </Section>

        {/* ─── 11. Founder Review ─── */}
        <Section n="11" title="Founder Review" icon={AlertOctagon} testId="sec-founder" agentEvent="founder_review">
          <blockquote className="border-l-2 border-teal-400/40 pl-4 my-4">
            <Quote className="w-5 h-5 text-teal-300/40 mb-1" />
            <p className="text-lg italic leading-relaxed">"{fv.overall_recommendation}"</p>
            <footer className="text-[10px] uppercase tracking-[0.18em] text-teal-300/70 mt-2">— Aignoz Founder Review AI · confidence {fv.confidence_score}</footer>
          </blockquote>

          <SubLabel>Risk Assessment</SubLabel>
          <div className="grid md:grid-cols-2 gap-3 mt-2">
            {[
              ["technical_risk", "Technical"],
              ["clinical_risk", "Clinical"],
              ["regulatory_risk", "Regulatory"],
              ["commercialization_risk", "Commercial"],
              ["scientific_risk", "Scientific"],
            ].map(([k, label]) => {
              const r = fv[k]; if (!r) return null;
              const c = SEV[r.severity] || {};
              return (
                <div key={k} className={`rounded-md border p-3 ${c.border || "border-white/[0.06]"} ${c.bg || ""}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-semibold">{label}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${c.text} border ${c.border}`}>{r.severity}</span>
                  </div>
                  <p className="text-xs text-white/70 leading-relaxed">{r.mitigation}</p>
                </div>
              );
            })}
          </div>

          {fv.top_3_improvements?.length > 0 && (
            <>
              <SubLabel>Top 3 Improvements</SubLabel>
              <ol className="mt-2 space-y-3">
                {fv.top_3_improvements.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full border border-teal-400/40 text-teal-200 text-sm flex items-center justify-center font-semibold">{i + 1}</span>
                    <span className="text-sm leading-relaxed text-white/85">{s}</span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </Section>

        {/* ─── 12. Venture Scorecard ─── */}
        <Section n="12" title="Venture Scorecard" icon={ListTree} testId="sec-scorecard" agentEvent="scorer">
          {(() => {
            const V3 = [
              ["disease_burden_score", "Disease Burden"],
              ["market_size_score", "Market Size"],
              ["scientific_feasibility_score", "Scientific Feasibility"],
              ["patentability_score", "Patentability"],
              ["funding_availability_score", "Funding Availability"],
            ];
            const hasV3 = V3.some(([k]) => sc[k] && typeof sc[k].score === "number");
            const total = hasV3 ? V3.reduce((s, [k]) => s + ((sc[k] && sc[k].score) || 0), 0) : 0;
            const sellableUSD = sc.sellable_idea_price_usd;
            const sellableINR = sc.sellable_idea_price_inr;
            const costSaved = sc.cost_saved_usd;
            const teamSize = sc.regulatory_team_size;
            if (!hasV3) {
              return (
                <div className="rounded-lg border border-white/[0.08] overflow-hidden" data-testid="scorecard-legacy">
                  <div className="px-4 py-3 text-[11px] italic text-white/55 border-b border-white/[0.06] bg-white/[0.02]">
                    Pre-engine run — showing legacy V2 8-axis scorecard.
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.18em] text-white/50 bg-white/[0.02]">
                      <tr>
                        <th className="text-left px-4 py-2.5">Axis</th>
                        <th className="text-right px-4 py-2.5 w-24">Score</th>
                        <th className="text-left px-4 py-2.5">Rationale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SCORE_AXES.map(([k, label], i) => {
                        const s = sc[k] || {};
                        return (
                          <tr key={k} className={i % 2 ? "bg-white/[0.012]" : ""}>
                            <td className="px-4 py-2 font-medium text-white/90">{label}</td>
                            <td className="px-4 py-2 text-right mono text-teal-200 font-semibold">{s.score ?? "—"}</td>
                            <td className="px-4 py-2 text-white/65 text-[13px] leading-snug">{s.rationale || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            }
            return (
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-7 rounded-lg border border-white/[0.08] p-5" data-testid="report-v3-scorecard">
                  <div className="flex items-baseline justify-between mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80">Aignoz 5-Criteria Venture Filter</div>
                      <div className="text-xs text-white/55 italic">5 × 20 = 100</div>
                    </div>
                    <div className="text-2xl mono font-semibold text-teal-100" data-testid="report-venture-filter-total">
                      {total}<span className="text-sm text-white/40">/100</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {V3.map(([k, label]) => {
                      const v = sc[k] || {};
                      const score = v.score || 0;
                      const pct = (score / 20) * 100;
                      return (
                        <div key={k} data-testid={`report-v3-bar-${k}`}>
                          <div className="flex items-baseline justify-between mb-0.5">
                            <span className="text-sm font-medium text-white/85">{label}</span>
                            <span className="text-sm mono text-teal-200 font-semibold">{score}<span className="text-white/40">/20</span></span>
                          </div>
                          <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-teal-400/70 to-cyan-300/70" style={{ width: `${pct}%` }} />
                          </div>
                          {v.rationale && (
                            <div className="text-[11px] text-white/50 mt-1 leading-snug">{v.rationale}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-5 flex flex-col gap-3">
                  {(sellableUSD != null) ? (
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="rounded-lg p-5 border cursor-help"
                            style={{ background: "linear-gradient(135deg, rgba(209,126,192,0.10), rgba(94,234,212,0.04))", borderColor: "rgba(209,126,192,0.35)" }}
                            data-testid="report-sellable-price-card"
                          >
                            <div className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: "#F0B6DD" }}>Sellable Idea Price</div>
                            <div className="text-3xl mono font-semibold text-white mt-1" data-testid="report-sellable-price-usd">{formatUSD(sellableUSD)}</div>
                            <div className="text-base mono mt-0.5" style={{ color: "#F0B6DD" }} data-testid="report-sellable-price-inr">
                              {sellableINR != null ? `₹${Math.round(sellableINR).toLocaleString("en-IN")}` : formatINR(sellableUSD)}
                            </div>
                            <div className="text-[10px] text-white/45 mt-2 italic">Cost saved ÷ regulatory team size</div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-xs text-xs">
                          Cost saved by Aignoz OS ({formatUSD(costSaved)}) ÷ Regulatory team size ({teamSize}) = sellable price per idea.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : null}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.015] p-3">
                      <div className="text-[9px] uppercase tracking-[0.14em] text-white/40">Cost saved</div>
                      <div className="text-base mono font-semibold mt-0.5 text-teal-200">{formatUSD(costSaved)}</div>
                      <div className="text-[10px] mono text-white/45">{formatINR(costSaved)}</div>
                    </div>
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.015] p-3">
                      <div className="text-[9px] uppercase tracking-[0.14em] text-white/40">Reg. team</div>
                      <div className="text-base mono font-semibold mt-0.5 text-teal-200">{teamSize ?? "—"}</div>
                      <div className="text-[10px] text-white/45">headcount</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </Section>

        {/* ─── 13. Pitch Deck Summary ─── */}
        <Section n="13" title="Pitch Deck Summary" icon={FileText} testId="sec-pitch-summary" agentEvent="final_report_compiler">
          <div className="space-y-5">
            {[
              ["Problem", pds.problem],
              ["Solution", pds.solution],
              ["Technology", pds.technology],
              ["Market Opportunity", pds.market_opportunity],
              ["Validation Plan", pds.validation_plan],
              ["Regulatory Path", pds.regulatory_path],
              ["Business Model", pds.business_model],
              ["Risks", pds.risks],
              ["Why Now", pds.why_now],
            ].map(([label, value]) => value ? (
              <div key={label}>
                <SubLabel>{label}</SubLabel>
                <p className="prose">{value}</p>
              </div>
            ) : null)}
          </div>
        </Section>

        {/* ─── Footer ─── */}
        <Divider />
        <footer className="report-footer text-center mt-12 mb-6 space-y-1" data-testid="report-footer">
          <div className="text-sm font-medium text-white/85">Generated by Aignoz Innovation OS</div>
          <div className="text-xs text-white/55">Healthcare DeepTech Innovation Engine</div>
          <div className="text-[10px] mono text-white/30 mt-3">
            Run ID: {id} · Generated {dateLabel || "—"}
          </div>
          <div className="text-[10px] text-white/30 italic max-w-md mx-auto mt-3 leading-relaxed">
            Citations are LLM-generated and have not been independently verified.
          </div>
        </footer>
      </article>
      </ReportDocContext.Provider>
    </div>
  );
}

// ─── Small primitives ───
function HeaderStat({ label, value, small = false }) {
  return (
    <div className="rounded-md border border-white/[0.06] bg-white/[0.015] p-2">
      <div className="text-[9px] uppercase tracking-[0.14em] text-white/40">{label}</div>
      <div className={`mono font-semibold text-teal-200 mt-0.5 ${small ? "text-[11px] leading-tight" : "text-xl"}`}>
        {value != null && value !== "" ? value : "—"}
      </div>
    </div>
  );
}
function Divider() { return <div className="my-10 border-t border-white/[0.06]" />; }
function Section({ n, title, icon: Icon, testId, agentEvent, children }) {
  const doc = useContext(ReportDocContext);
  const resolved = agentEvent && doc ? resolveLayer(doc, agentEvent) : null;
  const info = resolved ? LAYER_INFO[resolved.layer] || LAYER_INFO.meta : null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 4 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.3 }}
      data-testid={testId}
      className="report-section mb-16">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <span className="mono text-[11px] text-teal-300/60">§{n}</span>
        <Icon className="w-4 h-4 text-teal-300/60" />
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        {info && (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] uppercase tracking-[0.14em] font-medium ${info.badgeClass}`}
            style={info.badgeStyle}
            data-testid={`report-layer-badge-${agentEvent}`}
          >
            <span className={`w-1 h-1 rounded-full ${info.dotClass}`} style={info.dotStyle} />
            {info.n != null ? `L${info.n} · ${info.label}` : info.label}
          </span>
        )}
      </div>
      {resolved?.layer_contribution && (
        <div className="text-[11px] italic text-white/45 leading-snug mb-4 print:text-[10px]">
          {resolved.layer_contribution}
        </div>
      )}
      {!resolved?.layer_contribution && resolved?.isLegacy && agentEvent && (
        <div className="text-[10px] italic text-white/30 mb-4" data-testid={`report-legacy-${agentEvent}`}>
          Legacy run — layer contribution not recorded
        </div>
      )}
      <div className="text-[15px]">{children}</div>
    </motion.section>
  );
}
function SubLabel({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-6 mb-2">{children}</div>;
}
function Box({ title, children }) {
  return (
    <div className="rounded-md border border-white/[0.06] bg-white/[0.015] p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80 mb-2">{title}</div>
      <div className="text-sm text-white/85 leading-relaxed">{children}</div>
    </div>
  );
}
function KpiTile({ label, value, subValue, accent, small = false }) {
  const colorMap = { teal: "text-teal-200", sky: "text-sky-200", cyan: "text-cyan-200" };
  return (
    <div className="rounded-md border border-white/[0.08] bg-white/[0.015] p-3">
      <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
      <div className={`mono font-semibold mt-1 ${small ? "text-sm leading-tight line-clamp-2" : "text-2xl"} ${colorMap[accent] || "text-white/85"}`}>{value ?? "—"}</div>
      {subValue && <div className="text-xs text-white/50 mono mt-0.5">{subValue}</div>}
    </div>
  );
}
